import { priceWithSiblingDiscount } from './pricing';
import type { CrossSchoolSiblingInfo } from './siblingDiscount';
import { MealPackage, OrderPerson } from '../types';

type PackagePriceData = {
    price: number;
    isSibling: boolean;
    recipientIsStaff: boolean;
};

type CalculatePackagePricesParams = {
    userPackagesList: Array<{ recipient: string }>;
    students: { [key: string]: OrderPerson } | null;
    mealPackage: MealPackage | null;
    samePackages: Array<{ recipient: string }>;
    discountSiblings: number;
    crossSchoolSiblingInfo: CrossSchoolSiblingInfo | null;
};

/**
 * Shared function to calculate package prices with sibling discounts
 * Used by both frontend and backend to ensure consistency
 * Works with plain objects only - callers should convert DataSnapshot to plain objects before calling
 */
export function calculatePackagePrices({
    userPackagesList,
    students,
    mealPackage,
    samePackages,
    discountSiblings,
    crossSchoolSiblingInfo
}: CalculatePackagePricesParams): { totalPrice: number; packagePrices: PackagePriceData[] } {
    if (!students || !mealPackage) {
        return { totalPrice: 0, packagePrices: [] };
    }

    let totalPrice = 0;
    const packagePrices: PackagePriceData[] = [];

    for (let i = 0; i < userPackagesList.length; i++) {
        const userPackage = userPackagesList[i];
        const recipientId = userPackage.recipient;
        const recipientData = students[recipientId] || null;
        const recipientIsStaff = !!recipientData?.personnel;

        // Apply sibling discount for students only (staff already get discounted priceStaff)
        let isSibling = false;
        if (!recipientIsStaff) {
            // Count how many student packages come before this one in current session
            let studentCount = 0;
            for (let j = 0; j < i; j++) {
                const prevRecipientId = userPackagesList[j].recipient;
                const prevRecipientData = students[prevRecipientId];
                if (prevRecipientData && !prevRecipientData.personnel) {
                    studentCount++;
                }
            }

            // Also check if there are already paid student packages for the same period
            const hasPaidStudentPackage = samePackages.some(pp => {
                const siblingRecipientId = pp.recipient;
                const siblingRecipientData = students[siblingRecipientId];
                return siblingRecipientData && !siblingRecipientData.personnel;
            });

            // Check for cross-school sibling discount
            const hasCrossSchoolSibling = crossSchoolSiblingInfo?.hasSiblingInOtherSchool;
            isSibling = studentCount > 0 || hasPaidStudentPackage || hasCrossSchoolSibling;
        }

        const packPrice = recipientIsStaff ? mealPackage.priceStaff : mealPackage.priceStudent;
        // Use the same function as backend to ensure consistency
        const packagePrice = priceWithSiblingDiscount(packPrice, recipientIsStaff, discountSiblings, isSibling);

        packagePrices.push({
            price: packagePrice,
            isSibling,
            recipientIsStaff
        });
        totalPrice += packagePrice;
    }

    return { totalPrice, packagePrices };
}
